import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Styles from './BrandIntelligence.module.scss';
import ProgressStepper from 'Components/BrandIntelligence/ProgressStepper/ProgressStepper';
import Step0BrandOverview from 'Components/BrandIntelligence/Step0BrandOverview/Step0BrandOverview';
import Step1Foundation from 'Components/BrandIntelligence/Step1Foundation/Step1Foundation';
import Step2WebsiteAnalysis from 'Components/BrandIntelligence/Step2WebsiteAnalysis/Step2WebsiteAnalysis';
import Step6SocialChannels from 'Components/BrandIntelligence/Step6SocialChannels/Step6SocialChannels';
import Step5ContentTraining from 'Components/BrandIntelligence/Step5ContentTraining/Step5ContentTraining';
import Step7FinalSettings from 'Components/BrandIntelligence/Step7FinalSettings/Step7FinalSettings';
import Step4CustomerVoice from 'Components/BrandIntelligence/Step4CustomerVoice/Step4CustomerVoice';
import onBoardingService from 'services/onBoardingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';
import { useDispatch } from 'react-redux';
import { actions } from 'redux/slices/auth';
import LoadingState from 'Components/LoadingState/LoadingState';
import { FiArrowLeft } from 'react-icons/fi';

// Helper to build profile URL from platform and handle
const buildProfileUrl = (platform, handle) => {
  if (!handle) return '';
  const cleanHandle = handle.replace('@', '');
  switch (platform) {
    case 'twitter':
      return `https://twitter.com/${cleanHandle}`;
    case 'linkedin':
      return `https://linkedin.com/in/${cleanHandle}`;
    case 'instagram':
      return `https://instagram.com/${cleanHandle}`;
    case 'facebook':
      return `https://facebook.com/${cleanHandle}`;
    case 'youtube':
      return `https://youtube.com/@${cleanHandle}`;
    case 'tiktok':
      return `https://tiktok.com/@${cleanHandle}`;
    default:
      return handle;
  }
};

const BrandIntelligence = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { brandId: urlBrandId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(!!urlBrandId);
  const [isEditMode, setIsEditMode] = useState(!!urlBrandId);
  const [brandId, setBrandId] = useState(urlBrandId || null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [brandData, setBrandData] = useState({
    // Step 1: Foundation
    brandType: 'BUSINESS',
    name: '',
    offering: '',
    marketPositioning: '',
    valueProposition: '',
    uniqueSellingPoint: '',

    // Step 2: Website Analysis
    websiteUrl: '',
    websiteAnalysisData: null,

    // Step 4: Customer Voice
    customerVoiceLinks: [],
    customerVoiceText: '',
    customerVoiceFiles: [],

    // Step 5: Content Training
    contentGoals: [],
    contentStructure: '',
    educationStyle: '',
    storytellingApproach: '',
    ctaStyle: '',
    contentSamples: [],
    contentThemes: [],

    // Step 6: Social Channels
    socialChannels: [],
    postingFrequency: '',

    // Step 7: Final Settings
    primaryBusinessGoal: 'BRAND_AWARENESS',
    contentFormats: [],
    creativityLevel: 0.7,
    customInstructions: '',
    additionalContext: '',
  });

  const updateBrandData = (data) => {
    setBrandData((prev) => ({ ...prev, ...data }));
  };

  // Load existing brand data when in edit mode
  useEffect(() => {
    const loadBrandData = async () => {
      if (!urlBrandId) return;

      try {
        setIsLoading(true);
        const response = await onBoardingService.getBrandProfile(urlBrandId);

        if (response.data.success) {
          const { brand_profile, business, content_guideline, content_samples, social_channels } = response.data.data;

          // Map backend data to frontend state structure
          const metadata = brand_profile.metadata || {};
          const mappedData = {
            // Brand profile reference (needed by Step 2 for business_id)
            brandProfile: {
              id: brand_profile.id,
              business_id: brand_profile.business_id,
            },
            // Step 1: Foundation
            brandType: brand_profile.brand_type?.toUpperCase() || 'BUSINESS',
            name: brand_profile.brand_name || '',
            offering: brand_profile.mission_statement || business?.description || '',
            marketPositioning: metadata.market_positioning || '',
            valueProposition: brand_profile.unique_value_proposition || '',
            uniqueSellingPoint: metadata.unique_selling_point || '',

            // Step 2: Website Analysis — read from brand_profile, fallback to business for legacy
            websiteUrl: brand_profile.url || business?.url || '',
            websiteAnalysisData: (brand_profile.brand_description || brand_profile.core_messaging)
              ? {
                  brand_description: brand_profile.brand_description || '',
                  industries: brand_profile.industries || (brand_profile.industry ? [brand_profile.industry] : []),
                  value_proposition: brand_profile.unique_value_proposition || '',
                  core_messaging: brand_profile.core_messaging || '',
                  tone: brand_profile.voice_tone || [],
                  target_audience: brand_profile.target_audience || (brand_profile.target_audience_description ? brand_profile.target_audience_description.split(', ').filter(Boolean) : []),
                  keywords_and_themes: brand_profile.keywords_and_themes || [],
                  detailed_topics: brand_profile.content_topics || [],
                }
              : business?.auto_generated_summary || null,

            // Step 4: Customer Voice
            customerVoiceLinks: metadata.customer_voice_links || [],
            customerVoiceText: brand_profile.target_audience_description || '',
            customerVoiceAnalysis: metadata.customer_voice_analysis || null,
            customerVoiceFiles: [],

            // Step 5: Content Training
            contentGoals: content_guideline?.content_themes?.goals || [],
            contentStructure: content_guideline?.sentence_structure_preferences || '',
            educationStyle: metadata.education_style || '',
            storytellingApproach: metadata.storytelling_approach || '',
            ctaStyle: metadata.cta_style || '',
            contentSamples: content_samples || [],
            contentThemes: content_guideline?.content_themes?.topics || business?.content_topics || [],

            // Step 6: Social Channels - map API data to form structure
            socialChannels: (social_channels || []).map(channel => ({
              platform: channel.platform,
              url: channel.handle ? buildProfileUrl(channel.platform, channel.handle) : '',
              purpose: channel.platform_specific_settings?.purpose || '',
              // Keep extra data for display
              handle: channel.handle,
              platform_specific_settings: channel.platform_specific_settings
            })),
            postingFrequency: metadata.posting_frequency || '',

            // Step 7: Final Settings
            primaryBusinessGoal: metadata.primary_business_goal || 'BRAND_AWARENESS',
            contentFormats: [],
            creativityLevel: metadata.creativity_level != null ? metadata.creativity_level : 0.7,
            customInstructions: metadata.custom_instructions || '',
            additionalContext: metadata.additional_context || '',
          };

          setBrandData((prev) => ({ ...prev, ...mappedData }));
          // Use brand_profile.id from response, not urlBrandId (which could be business ID)
          setBrandId(brand_profile.id);
        }
      } catch (error) {
        console.error('Error loading brand data:', error);
        errorToast('Failed to load brand data');
      } finally {
        setIsLoading(false);
      }
    };

    loadBrandData();
  }, [urlBrandId]);

  // Check if brand profile was already created (from website analysis)
  useEffect(() => {
    if (!brandId && brandData.brandProfile && brandData.brandProfile.id) {
      console.log('Loading existing brand profile ID:', brandData.brandProfile.id);
      setBrandId(brandData.brandProfile.id);
    }
  }, [brandData.brandProfile, brandId]);

  const handleNext = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepNumber) => {
    setCurrentStep(stepNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Save step-specific data to backend
  const saveStepData = async (stepNumber, stepData) => {
    if (!brandId) {
      console.warn('No brandId, cannot save step data');
      return { success: false, error: 'No brand profile exists yet' };
    }

    try {
      // Map step data to API payload based on step number
      let payload = { completion_step: stepNumber };

      switch (stepNumber) {
        case 2: // Brand Foundation
          payload = {
            ...payload,
            brand_name: stepData.name,
            brand_type: stepData.brandType,
            mission_statement: stepData.offering,
            unique_value_proposition: stepData.valueProposition,
            user_entered_summary: {
              market_positioning: stepData.marketPositioning,
              unique_selling_point: stepData.uniqueSellingPoint,
            },
          };
          break;
        case 3: // Website Analysis
          payload = {
            ...payload,
            website_url: stepData.websiteUrl,
            brand_description: stepData.websiteAnalysisData?.brand_description || '',
            core_messaging: stepData.websiteAnalysisData?.core_messaging || '',
            voice_tone: stepData.websiteAnalysisData?.tone || [],
            keywords_and_themes: stepData.websiteAnalysisData?.keywords_and_themes || [],
            content_topics: stepData.websiteAnalysisData?.detailed_topics || [],
            industries: stepData.websiteAnalysisData?.industries || [],
            unique_value_proposition: stepData.websiteAnalysisData?.value_proposition || '',
            target_audience: stepData.websiteAnalysisData?.target_audience || [],
          };
          break;
        case 4: // Social Channels
          payload = {
            ...payload,
            social_channels: (stepData.socialChannels || []).map(ch => ({
              platform: ch.platform,
              purpose: ch.purpose || '',
            })),
          };
          break;
        case 5: // Content Training
          payload = {
            ...payload,
            content_goals: stepData.contentGoals,
            content_structure: stepData.contentStructure,
            content_themes: stepData.contentThemes,
            education_style: stepData.educationStyle,
            storytelling_approach: stepData.storytellingApproach,
            cta_style: stepData.ctaStyle,
          };
          break;
        case 6: // Customer Voice / Audience
          payload = {
            ...payload,
            target_audience_description: stepData.customerVoiceText,
            metadata: {
              customer_voice_links: stepData.customerVoiceLinks || [],
              customer_voice_analysis: stepData.customerVoiceAnalysis || null,
            },
          };
          break;
        case 7: // Final Settings
          payload = {
            ...payload,
            status: 'completed',
            user_entered_summary: {
              custom_instructions: stepData.customInstructions,
              additional_context: stepData.additionalContext,
              creativity_level: stepData.creativityLevel,
              primary_business_goal: stepData.primaryBusinessGoal,
            },
          };
          break;
        default:
          break;
      }

      const response = await onBoardingService.updateBrandProfile(brandId, payload);

      if (response.data?.success) {
        return { success: true };
      } else {
        return { success: false, error: response.data?.error || 'Failed to save' };
      }
    } catch (error) {
      console.error('Error saving step data:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to save' };
    }
  };

  // Save ALL data from all steps (used by Save & Finish)
  const saveAllStepsData = async () => {
    if (!brandId) return { success: false, error: 'No brand profile' };

    try {
      const payload = {
        // Step 2: Foundation
        brand_name: brandData.name,
        brand_type: brandData.brandType,
        mission_statement: brandData.offering,
        unique_value_proposition: brandData.valueProposition,
        // Step 3: Website
        website_url: brandData.websiteUrl,
        // Step 5: Content Training
        content_goals: brandData.contentGoals,
        content_structure: brandData.contentStructure,
        education_style: brandData.educationStyle,
        storytelling_approach: brandData.storytellingApproach,
        cta_style: brandData.ctaStyle,
        content_themes: brandData.contentThemes,
        // Step 6: Audience
        target_audience_description: brandData.customerVoiceText,
        // Step 7: Final Settings
        custom_instructions: brandData.customInstructions,
        additional_context: brandData.additionalContext,
        // Mark as complete
        status: 'completed',
        completion_step: 7,
        // User entered summary (for Business table)
        user_entered_summary: {
          market_positioning: brandData.marketPositioning,
          unique_selling_point: brandData.uniqueSellingPoint,
          content_goals: brandData.contentGoals,
          content_structure: brandData.contentStructure,
          education_style: brandData.educationStyle,
          storytelling_approach: brandData.storytellingApproach,
          cta_style: brandData.ctaStyle,
          posting_frequency: brandData.postingFrequency,
          custom_instructions: brandData.customInstructions,
          additional_context: brandData.additionalContext,
          creativity_level: brandData.creativityLevel,
          primary_business_goal: brandData.primaryBusinessGoal,
        },
      };

      const response = await onBoardingService.updateBrandProfile(brandId, payload);
      return { success: response.data?.success };
    } catch (error) {
      console.error('Error saving all steps data:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle Save & Continue for steps 2-6
  // Accepts stepData directly from the component to avoid async state issues
  const handleSaveAndContinue = async (stepNumber, stepData) => {
    // Use provided stepData or fall back to brandData
    const dataToSave = stepData || brandData;
    const result = await saveStepData(stepNumber, dataToSave);
    if (result.success) {
      successToast('Progress saved!');
      handleNext();
    } else {
      errorToast(result.error || 'Failed to save progress');
    }
  };

  const createBrandProfile = async () => {
    if (!brandData.name || !brandData.offering) {
      errorToast('Please fill in required fields: Brand Name and Offering');
      setCurrentStep(1);
      throw new Error('Missing required fields');
    }

    try {
      // Step 1: Create Business (legacy system)
      const businessPayload = {
        brand_name: brandData.name,
        brand_type: brandData.brandType,
        url: brandData.websiteUrl || null,
        description: brandData.offering,
        content_goals: brandData.offering,
        user_entered_summary: {
          brand_name: brandData.name,
          brand_description: brandData.offering,
          market_positioning: brandData.marketPositioning,
          value_proposition: brandData.valueProposition,
          unique_selling_point: brandData.uniqueSellingPoint,
          content_goals: brandData.contentGoals,
          content_structure: brandData.contentStructure,
          education_style: brandData.educationStyle,
          storytelling_approach: brandData.storytellingApproach,
          cta_style: brandData.ctaStyle,
          posting_frequency: brandData.postingFrequency,
          custom_instructions: brandData.customInstructions,
          additional_context: brandData.additionalContext,
        },
        auto_generated_summary: brandData.websiteAnalysisData,
        industries: brandData.websiteAnalysisData?.industries || [],
        keywords_and_themes: brandData.websiteAnalysisData?.keywordsAndThemes || [],
        target_audience: brandData.websiteAnalysisData?.targetAudience || [],
        tone: brandData.websiteAnalysisData?.tone || [],
        content_topics: brandData.contentThemes,
        is_active: true,
        scraper_status: brandData.websiteAnalysisData ? 'completed' : null,
      };

      let businessResponse;
      if (brandId) {
        businessResponse = await onBoardingService.updateBusinessById(brandId, businessPayload);
      } else {
        businessResponse = await onBoardingService.createBusiness(businessPayload);
      }

      // Validate business response
      const businessId = businessResponse?.data?.id || businessResponse?.data?.data?.id;
      if (!businessId) {
        console.error('Invalid business response:', businessResponse?.data);
        throw new Error('Failed to create business - invalid response from server');
      }

      // Step 2: Create BrandProfile with ContentGuideline
      const brandProfilePayload = {
        brand_name: brandData.name,
        brand_type: brandData.brandType,
        business_id: businessId,
        description: brandData.offering,
        mission_statement: brandData.offering,
        value_proposition: brandData.valueProposition,
        unique_value_proposition: brandData.valueProposition,
        // Step 5 form data for ContentGuideline
        content_goals: brandData.contentGoals,
        content_structure: brandData.contentStructure,
        education_style: brandData.educationStyle,
        storytelling_approach: brandData.storytellingApproach,
        cta_style: brandData.ctaStyle,
        content_themes: brandData.contentThemes,
      };

      const brandProfileResponse = await onBoardingService.createBrandProfile(brandProfilePayload);

      // Validate brand profile response
      const brandProfileId = brandProfileResponse?.data?.data?.brand_profile?.id;
      if (!brandProfileId) {
        console.error('Invalid brand profile response:', brandProfileResponse?.data);
        throw new Error(brandProfileResponse?.data?.error || 'Failed to create brand profile - invalid response from server');
      }

      // Save brand profile ID to state
      setBrandId(brandProfileId);

      return brandProfileId;
    } catch (error) {
      console.error('Error creating brand profile:', error);
      errorToast(error.response?.data?.error || 'Failed to create brand profile. Please try again.');
      throw error;
    }
  };

  const handleComplete = async () => {
    setIsCreating(true);

    try {
      // brandId should always exist (Step 1 gate creates it)
      if (!brandId) {
        errorToast('Brand profile not found. Please start from Step 1.');
        setCurrentStep(1);
        setIsCreating(false);
        return;
      }

      // Save ALL data from all steps
      await saveAllStepsData();

      successToast(isEditMode ? 'Brand Intelligence updated successfully!' : 'Brand Intelligence saved successfully!');

      // Update user brands in Redux
      const brandsResponse = await onBoardingService.getUserBrandInfo();
      dispatch(actions.updateUser({ user: { user_brands: brandsResponse.data } }));

      // Navigate to brands page
      setTimeout(() => {
        navigate('/brand');
      }, 1000);

    } catch (error) {
      console.error('Error saving brand:', error);
      errorToast(error.response?.data?.error || 'Failed to save brand. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const renderStep = () => {
    // Tab order (7 steps):
    // 1. Brand Overview (Quick Start)
    // 2. Brand Foundation
    // 3. Website Analysis
    // 4. Social Channels
    // 5. Content Training
    // 6. Audience
    // 7. AI Settings (final)
    switch (currentStep) {
      case 1:
        return (
          <Step0BrandOverview
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onSaveAndExit={handleSaveAndExit}
            onBrandProfileCreated={(id) => {
              console.log('Brand Profile created with ID:', id);
              setBrandId(id);
            }}
          />
        );
      case 2:
        return (
          <Step1Foundation
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveAndExit={handleSaveAndExit}
            onSaveAndContinue={(stepData) => handleSaveAndContinue(2, stepData)}
          />
        );
      case 3:
        return (
          <Step2WebsiteAnalysis
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveAndExit={handleSaveAndExit}
            onSaveAndContinue={(stepData) => handleSaveAndContinue(3, stepData)}
            onBrandProfileCreated={(id) => {
              console.log('Brand Profile created with ID:', id);
              setBrandId(id);
            }}
          />
        );
      case 4:
        return (
          <Step6SocialChannels
            data={brandData}
            brandId={brandId}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveAndExit={handleSaveAndExit}
            onSaveAndContinue={(stepData) => handleSaveAndContinue(4, stepData)}
          />
        );
      case 5:
        return (
          <Step5ContentTraining
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            brandId={brandId}
            onSaveAndExit={handleSaveAndExit}
            onSaveAndContinue={(stepData) => handleSaveAndContinue(5, stepData)}
          />
        );
      case 6:
        return (
          <Step4CustomerVoice
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveAndExit={handleSaveAndExit}
            onSaveAndContinue={(stepData) => handleSaveAndContinue(6, stepData)}
            brandId={brandId}
          />
        );
      case 7:
        return (
          <Step7FinalSettings
            data={brandData}
            onUpdate={updateBrandData}
            onNext={handleComplete}
            onPrevious={handlePrevious}
            onSaveAndExit={handleSaveAndExit}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={Styles.loadingContainer}>
        <LoadingState text="Loading brand data..." />
      </div>
    );
  }

  if (isCreating) {
    return (
      <div className={Styles.loadingContainer}>
        <LoadingState text={isEditMode ? "Updating your Brand Intelligence..." : "Saving your Brand Intelligence..."} />
      </div>
    );
  }

  const handleBackToHub = () => {
    navigate('/brand');
  };

  const handleSaveAndExit = async () => {
    // Save current state and navigate back to brands hub
    try {
      if (brandId) {
        // If brand exists, try to save current state
        await createBrandProfile();
        successToast('Progress saved successfully!');
      }
      navigate('/brand');
    } catch (error) {
      // Even if save fails, allow navigation
      console.error('Error saving on exit:', error);
      navigate('/brand');
    }
  };

  return (
    <div className={Styles.container}>
      {/* Main Layout: Sidebar + Content */}
      <div className={Styles.mainLayout}>
        {/* Vertical Navigation Sidebar */}
        <div className={Styles.sidebar}>
          {/* Back Button */}
          <button className={Styles.backButton} onClick={handleBackToHub}>
            <FiArrowLeft size={18} />
            <span>All Brands</span>
          </button>

          <ProgressStepper
            currentStep={currentStep}
            variant="vertical"
            onStepClick={handleStepClick}
            brandId={brandId}
          />
        </div>

        {/* Step Content */}
        <div className={Styles.content}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default BrandIntelligence;
